import { Flex, Spin, Tabs, TabsProps } from "antd";
import { useQuery } from "@tanstack/react-query";
import PageHeading from "components/PageHeading.component";
import UserProfileCard from "components/profile/UserProfile.component";
import { sessionsByInstructorV2Options } from "hooks/queries/booking-v2";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { useGetInstructorLoginCode } from "hooks/queries/staff";
import {
  useRegenerateInstructorLoginLink,
  useRegenerateInstructorLoginCode,
} from "hooks/mutations/staff";
import { useGetUserById } from "hooks/queries/user";
import { useAuth } from "context/AuthContextProvider";
import { hasRole, isManager, isSystemAdmin } from "shared/utils/role";
import { useTabParam } from "hooks/useTabParam";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { UserType } from "shared/types/enum";
import InstructorManagementTab from "./InstructorManagementTab";
import InstructorSessionHistory from "./InstructorSessionHistory";
import StaffRolesCard from "./StaffRolesCard";
import InstructorRoleToggleCard from "./InstructorRoleToggleCard";

const InstructorDetails = () => {
  const navigate = useNavigate();
  const { instructorId } = useParams();
  const { data: settings } = useBusinessSettings();
  const employeeManagementEnabled = !!settings?.employee_management_enabled;
  // The "Manajemen" tab only shows once the master is on AND at least one of
  // its 3 sub-flags is on — an empty tab with no sub-tabs inside would be a
  // dead end. See EmployeeManagementPage.tsx / InstructorManagementTab.tsx.
  const anyManagementTabEnabled =
    employeeManagementEnabled &&
    !!(
      settings?.employee_management_working_hours_enabled ||
      settings?.employee_management_leave_enabled ||
      settings?.employee_management_class_assignment_enabled
    );
  // Kelas and Manajemen are instructor-only tabs. Admin/Manager accounts are
  // opened through this same route (a Manager's Staf list, or Bisnis -> Akun
  // for a system admin) and have no sessions to list and no employee-
  // management data, so only the Profil tab applies to them. Their roles
  // never include "instructor" (see getDisplayUserType in
  // UserProfile.component.tsx), so the viewed account's roles decide. The
  // actor check stays alongside for the system admin, whose token also can't
  // call the studio-scoped queries below.
  const { state } = useAuth();
  const sysAdminActor = isSystemAdmin(state.user?.roles);
  const managerActor = !sysAdminActor && isManager(state.user?.roles);
  const {
    data: instructor,
    isLoading,
    isError,
  } = useGetUserById(instructorId ?? "", UserType.Instructor);
  // Both Kelas (class-teaching history) and Manajemen (Employee Management)
  // only make sense for someone who actually teaches — Manager/Admin staff
  // accounts reach this same /staff/:id route too (via /accounts' staff
  // list), and every staff type has a `staff` row regardless of role, so
  // "has a staff record" alone can't distinguish them. A Manager viewed here
  // would otherwise show a permanently-empty Kelas tab. Must check the
  // fetched user's actual roles, not just that a staff record exists.
  const isInstructor = hasRole(instructor?.roles, UserType.Instructor);
  const studioTabsVisible = !sysAdminActor && isInstructor;
  // Must mirror the "3" condition in `items` below EXACTLY (including
  // `instructor?.staff`) — a looser condition here would let `?tab=3`
  // resolve to a URL-valid activeKey with no matching `items` entry, which
  // AntD renders as a blank pane instead of falling back to "1". Kelas and
  // Manajemen are dropped from both this valid-key list and the rendered
  // items, so a stale ?tab=2 / ?tab=3 also falls back to Profil.
  const { activeKey, onChange } = useTabParam("1", "tab", [
    "1",
    ...(studioTabsVisible
      ? ["2", ...(anyManagementTabEnabled && instructor?.staff ? ["3"] : [])]
      : []),
  ]);
  // A system admin actor's token also has no business_id: the admin-only v2
  // /booking mount answers 401, which the axios interceptor treats as an
  // expired session and signs them out. Skip the request for them, and for
  // Admin/Manager targets who have no sessions anyway (the Kelas tab it
  // feeds doesn't even render for them, per studioTabsVisible above); the
  // settings query already skips itself the same way.
  const { data: sessions = [], isLoading: isSessionsLoading } = useQuery({
    ...sessionsByInstructorV2Options(instructor?.id),
    enabled: !!instructor?.id && studioTabsVisible,
  });
  const { data: loginCredentials } = useGetInstructorLoginCode(
    instructorId ?? ""
  );
  // The server only returns a link for real instructors (admin/manager
  // accounts opened via this route get link: null), so the card's link row
  // and regenerate button hide themselves for those targets.
  const regenerateLoginLink = useRegenerateInstructorLoginLink();
  const regenerateLoginCode = useRegenerateInstructorLoginCode();

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: translate("tabs.profile"),
      children: instructor && (
        <Flex vertical gap={16}>
          <UserProfileCard
            user={instructor}
            thisUserType={UserType.Instructor}
            loginCode={loginCredentials?.code ?? null}
            loginLink={loginCredentials?.link ?? null}
            onRegenerateLoginLink={() =>
              instructorId && regenerateLoginLink.mutate(instructorId)
            }
            isRegeneratingLoginLink={regenerateLoginLink.isPending}
            onRegenerateLoginCode={() =>
              instructorId && regenerateLoginCode.mutate(instructorId)
            }
            isRegeneratingLoginCode={regenerateLoginCode.isPending}
          />
          {/* Roles are Spott's to change (PUT /v1/admin/... is system-admin
              only), so the editor renders for that actor alone; a studio
              admin adds the instructor role through Tambah Instruktur. */}
          {sysAdminActor && <StaffRolesCard user={instructor} />}
          {/* The studio Manager's own switch, for any admin-tier account of
              their studio, peer Managers and themselves included. Reached
              from the Staf page (their own row too); Profil Aku carries the
              same card. */}
          {managerActor && hasRole(instructor.roles, UserType.Admin) && (
              <InstructorRoleToggleCard user={instructor} />
            )}
        </Flex>
      ),
    },
    // Studio-side, instructor-only tabs — a system admin sees Profil alone
    // (see above), and so does a Manager/Admin viewed via /accounts (Kelas
    // would just be a permanently-empty class-teaching history for them).
    ...(studioTabsVisible
      ? [
          {
            key: "2",
            label: translate("tabs.class_history"),
            children: instructor && (
              <InstructorSessionHistory
                sessions={sessions}
                isLoading={isSessionsLoading}
              />
            ),
          },
          // Only rendered when the studio has turned on Employee Management AND
          // at least one of its 3 sub-flags (Pengaturan > Manajemen Karyawan) —
          // keeps the feature fully hidden otherwise. Grouped under one tab (not
          // three flat ones) so "Kelas" above and "Kelas yang Ditugaskan" inside
          // here don't sit side by side in the same tab bar.
          ...(anyManagementTabEnabled && instructor?.staff
            ? [
                {
                  key: "3",
                  label: translate("tabs.employee_management"),
                  children: (
                    <InstructorManagementTab
                      staffId={instructor.id}
                      staff={instructor.staff}
                    />
                  ),
                },
              ]
            : []),
        ]
      : []),
  ];

  // If retrieving is error throw to not found.
  useEffect(() => {
    if (isError) navigate(PATHS.NOT_FOUND);
    // eslint-disable-next-line
  }, [isError]);

  return (
    <>
      {isLoading ? (
        <Flex
          style={{
            justifyContent: "center",
            alignItems: "center",
            height: "100vh ",
          }}
        >
          <Spin />
        </Flex>
      ) : (
        <>
          <PageHeading title={translate("title.staff_detail")} />
          <Tabs activeKey={activeKey} size="small" items={items} onChange={onChange} />
        </>
      )}
    </>
  );
};

export default InstructorDetails;
